// LineLiffCallback.container: completes LINE LIFF login then exchanges tokens for Wannabes session redirect.
// Mistake: LIFF flow previously mixed code-based callback fallbacks; keep only init→login→init→token→backend verify→redirect.
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";

type Flow = "login" | "signup";

const LineLiffCallbackContainer = () => {
    const location = useLocation();
    const [error, setError] = useState<string>("");

    const flow = useMemo<Flow>(() => {
        const f = new URLSearchParams(location.search).get("flow");
        return f === "signup" ? "signup" : "login";
    }, [location.search]);

    const schoolId = useMemo(() => {
        const v = new URLSearchParams(location.search).get("school-id");
        return v && /^\d+$/.test(v) ? v : null;
    }, [location.search]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const liffId = (process.env.REACT_APP_LINE_LIFF_ID || "").trim();
                const authBase = (process.env.REACT_APP_AUTH_URL || "").replace(/\/+$/, "");
                if (!liffId || !authBase) {
                    throw new Error(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupPre.oauthNotReady"));
                }

                const liff = (await import("@line/liff")).default;
                await liff.init({ liffId, withLoginOnExternalBrowser: true });

                if (!liff.isLoggedIn()) {
                    liff.login({ redirectUri: window.location.href });
                    return;
                }

                const idToken = liff.getIDToken() || "";
                const accessToken = liff.getAccessToken() || "";
                if (!idToken && !accessToken) {
                    throw new Error("OAUTH_LINE_IDTOKEN_REQUIRED");
                }

                console.log("LINE_LIFF:verify:start", {
                    authBase,
                    endpoint: `${authBase}/auth/social/line-liff/verify`,
                    flow,
                    schoolId,
                    hasIdToken: Boolean(idToken),
                    hasAccessToken: Boolean(accessToken),
                    idTokenLen: idToken.length,
                    accessTokenLen: accessToken.length,
                });
                const res = await fetch(`${authBase}/auth/social/line-liff/verify`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        flow,
                        schoolId,
                        idToken: idToken || undefined,
                        accessToken: accessToken || undefined,
                    }),
                });
                console.log("LINE_LIFF:verify:response", { status: res.status, ok: res.ok });
                const json = await res.json().catch(() => ({}));
                if (cancelled) {
                    return;
                }
                if (!res.ok || !json?.redirectUrl) {
                    const code = json?.errorCode ? String(json.errorCode) : "LINE_VERIFY_FAILED";
                    throw new Error(code);
                }
                console.log("LINE_LIFF:verify:redirect", { redirectUrl: String(json.redirectUrl) });
                window.location.href = String(json.redirectUrl);
            } catch (e: any) {
                if (cancelled) {
                    return;
                }
                setError(e?.message || "LINE_LIFF_FAILED");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [flow, schoolId]);

    if (error) {
        return (
            <main className="min-h-screen flex items-center justify-center px-4">
                <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 text-center">
                    <h2 className="text-lg font-semibold text-gray-900">LINE</h2>
                    <p className="mt-2 text-sm text-gray-700 break-words">{error}</p>
                </div>
            </main>
        );
    }

    return (
        <main className="min-h-screen flex items-center justify-center px-4">
            <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 text-center">
                <h2 className="text-lg font-semibold text-gray-900">LINE</h2>
                <p className="mt-2 text-sm text-gray-700">{getLanguageValue(PAGE_TYPE.COMMON, "appInit.txtPleaseWait")}</p>
            </div>
        </main>
    );
};

export default LineLiffCallbackContainer;

